import { useId, useState } from 'react';
import {
  Checkbox,
  Input,
  InputDecoration,
  InputGroup,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
} from '@monksflow/monks-ui';
import { Building2Icon, LockIcon, SearchIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  useUpdateTrackerScopeMutation,
  type TrackerBoardType,
  type TrackerScope,
} from '@/entities/tracker';
import { notificationService } from '@/lib/services/notification.service';
import { track } from '@/shared/analytics';

import { useWorkspaceScope } from '../../lib/useWorkspaceScope';

/** Above this many workspaces the checkbox list gets a search box. */
const SEARCH_ABOVE = 8;

const CHIP_CLASS = [
  'inline-flex h-9 max-w-65 min-w-0 items-center gap-1.5 rounded-md border border-border pl-3 pr-2 text-sm text-foreground',
  'outline-none focus-visible:ring-2 focus-visible:ring-ring',
].join(' ');

/**
 * Which workspaces a record board reads its rows from, for the person viewing
 * it. Nothing ticked means every workspace they can read. Every change is
 * saved as it is made.
 */
export function WorkspaceScopeChip({
  trackerId,
  boardType,
}: {
  trackerId: string;
  boardType: TrackerBoardType;
}) {
  const { t } = useTranslation('trackers');
  const { workspaceId, scope } = useWorkspaceScope(trackerId);
  const update = useUpdateTrackerScopeMutation({ workspaceId, trackerId });
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const baseId = useId();

  if (!scope) return null;

  const nameOf = (workspace: TrackerScope['available'][number]) =>
    workspace.name ?? t('trackersBoard.workspaceScope.unnamedWorkspace');

  if (scope.locked) {
    const name = scope.available[0] ? nameOf(scope.available[0]) : '';
    return (
      <button
        type="button"
        disabled
        aria-label={t('trackersBoard.workspaceScope.locked', { name })}
        className={`${CHIP_CLASS} bg-card`}
      >
        <LockIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="shrink-0 text-muted-foreground">
          {t('trackersBoard.workspaceScope.lockedPrefix')}
        </span>
        <span className="min-w-0 truncate font-medium">{name}</span>
      </button>
    );
  }

  const available = scope.available;
  const listedIds = new Set(available.map((workspace) => workspace.id));
  // While a change is in flight the controls show what was asked for, so the
  // list does not flick back to the old answer before the new one lands.
  const stored = update.isPending ? update.variables.workspaceIds : scope.workspaceIds;
  const selected = stored.filter((id) => listedIds.has(id));
  const selectedIds = new Set(selected);
  // A stored workspace that is not listed has no checkbox to untick, so a change
  // made here keeps it, unless the change empties the selection back to all.
  const unlisted = stored.filter((id) => !listedIds.has(id));

  const save = (workspaceIds: string[]) => {
    update.mutate(
      { workspaceIds },
      {
        onSuccess: (next) =>
          track('tracker_scope_changed', {
            tracker_id: trackerId,
            board_type: boardType,
            workspace_count: next.resolvedWorkspaceIds.length,
          }),
        onError: () => notificationService.showError(t('trackersBoard.workspaceScope.notSaved')),
      },
    );
  };

  const toggleWorkspace = (id: string, checked: boolean) => {
    const next = checked ? [...selected, id] : selected.filter((candidate) => candidate !== id);
    // Every listed workspace ticked is saved as all, so a workspace joined later is read too.
    const everyListed = next.length >= available.length;
    save(next.length > 0 && !everyListed ? [...next, ...unlisted] : []);
  };

  const total = available.length;
  const value =
    selected.length === 0
      ? t('trackersBoard.workspaceScope.valueAll', { total })
      : t('trackersBoard.workspaceScope.valueSome', { count: selected.length, total });

  const searchable = available.length > SEARCH_ABOVE;
  const query = searchable ? search.trim().toLowerCase() : '';
  const visible = query
    ? available.filter((workspace) => nameOf(workspace).toLowerCase().includes(query))
    : available;

  const rowsFromId = `${baseId}-rows-from`;
  const allId = `${baseId}-all`;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('trackersBoard.workspaceScope.trigger', { value })}
          className={[
            CHIP_CLASS,
            'cursor-pointer transition-colors hover:bg-accent',
            selected.length > 0 ? 'bg-accent' : 'bg-card',
          ].join(' ')}
        >
          <Building2Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="shrink-0 text-muted-foreground">
            {t('trackersBoard.workspaceScope.triggerPrefix')}
          </span>
          <span className="min-w-0 truncate font-medium">{value}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" aria-labelledby={rowsFromId} className="flex w-65 flex-col p-0">
        <div className="flex flex-col gap-2 p-3">
          <p id={rowsFromId} className="text-xs font-medium text-muted-foreground">
            {t('trackersBoard.workspaceScope.rowsFrom')}
          </p>
          <div className="flex min-w-0 items-center gap-2">
            <Checkbox
              id={allId}
              checked={selected.length === 0}
              disabled={update.isPending}
              onCheckedChange={(checked) => {
                if (checked === true && selected.length > 0) save([]);
              }}
            />
            <Label htmlFor={allId} className="min-w-0 truncate font-normal">
              {t('trackersBoard.workspaceScope.all')}
            </Label>
          </div>
        </div>
        <Separator />
        <div role="group" aria-labelledby={rowsFromId} className="flex flex-col gap-2 p-3">
          {searchable ? (
            <InputGroup size="sm">
              <InputDecoration muted>
                <SearchIcon className="size-3.5" />
              </InputDecoration>
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('trackersBoard.workspaceScope.searchPlaceholder')}
                aria-label={t('trackersBoard.workspaceScope.search')}
              />
            </InputGroup>
          ) : null}
          <div className="flex max-h-60 flex-col gap-2 overflow-y-auto">
            {visible.map((workspace) => {
              const checkboxId = `${baseId}-ws-${workspace.id}`;
              return (
                <div key={workspace.id} className="flex min-w-0 items-center gap-2">
                  <Checkbox
                    id={checkboxId}
                    checked={selectedIds.has(workspace.id)}
                    disabled={update.isPending}
                    onCheckedChange={(checked) => toggleWorkspace(workspace.id, checked === true)}
                  />
                  <Label htmlFor={checkboxId} className="min-w-0 truncate font-normal">
                    {nameOf(workspace)}
                  </Label>
                </div>
              );
            })}
            {visible.length === 0 ? (
              <p className="py-2 text-center text-sm text-muted-foreground">
                {t('trackersBoard.workspaceScope.noMatches')}
              </p>
            ) : null}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
